import { useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { useImportProjects } from "@/hooks/use-monday-import";
import type {
  CommentPreviewItem,
  CommentImportOverride,
} from "@/hooks/use-monday-comments-import";

const NONE_VALUE = "__none__";

const MATCH_BADGE: Record<string, string> = {
  exact: "bg-green-100 text-green-700",
  override: "bg-blue-100 text-blue-700",
  project_only: "bg-orange-100 text-orange-700",
  skipped: "bg-gray-100 text-gray-600",
};

interface Props {
  preview: CommentPreviewItem[];
  overrides: Record<string, CommentImportOverride>;
  onOverrideChange: (itemKey: string, patch: CommentImportOverride) => void;
}

/**
 * Tabell for å redigere kommentar-import før bekreft. Items uten automatisk
 * project_code-match får en dropdown for manuelt valg av prosjekt. Admin
 * kan også slå av enkeltlinjer (skip-toggle).
 */
export function CommentImportPreviewTable({
  preview,
  overrides,
  onOverrideChange,
}: Props) {
  const [showMatched, setShowMatched] = useState(false);
  const { data: projects = [] } = useImportProjects();

  const grouped = useMemo(() => {
    const matched: CommentPreviewItem[] = [];
    const needsAttention: CommentPreviewItem[] = [];
    for (const p of preview) {
      if (p.match_level === "exact") matched.push(p);
      else needsAttention.push(p);
    }
    return { matched, needsAttention };
  }, [preview]);

  return (
    <div className="space-y-4">
      {grouped.needsAttention.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">
            Trenger justering ({grouped.needsAttention.length})
          </h4>
          <p className="text-[11px] text-muted-foreground mb-2">
            Velg prosjekt manuelt eller slå av linjen før import.
          </p>
          <ItemTable
            items={grouped.needsAttention}
            overrides={overrides}
            onOverrideChange={onOverrideChange}
            projects={projects}
          />
        </div>
      )}

      {grouped.matched.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowMatched((v) => !v)}
            className="text-sm font-medium text-green-700 hover:underline flex items-center gap-1"
          >
            <SrIcon
              name={showMatched ? "chevron-down" : "chevron-right"}
              className="w-3.5 h-3.5"
            />
            {grouped.matched.length} perfekt matchet — {showMatched ? "skjul" : "vis"}
          </button>
          {showMatched && (
            <div className="mt-2">
              <ItemTable
                items={grouped.matched}
                overrides={overrides}
                onOverrideChange={onOverrideChange}
                projects={projects}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ItemTableProps {
  items: CommentPreviewItem[];
  overrides: Record<string, CommentImportOverride>;
  onOverrideChange: (itemKey: string, patch: CommentImportOverride) => void;
  projects: Array<{ id: string; project_code: string; name: string | null }>;
}

function ItemTable({ items, overrides, onOverrideChange, projects }: ItemTableProps) {
  return (
    <div className="overflow-x-auto border rounded-md">
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr className="border-b text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="text-left py-2 px-2 font-medium">Item</th>
            <th className="text-left py-2 px-2 font-medium w-[80px]">Kode</th>
            <th className="text-left py-2 px-2 font-medium w-[260px]">Prosjekt</th>
            <th className="text-right py-2 px-2 font-medium w-[80px]">Oppdat.</th>
            <th className="text-right py-2 px-2 font-medium w-[60px]">Svar</th>
            <th className="text-right py-2 px-2 font-medium w-[70px]">Vedlegg</th>
            <th className="text-right py-2 px-2 font-medium w-[60px]">Likes</th>
            <th className="text-center py-2 px-2 font-medium w-[60px]">Skip</th>
            <th className="text-left py-2 px-2 font-medium w-[100px]">Match</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const key = String(item.monday_item_id);
            const override = overrides[key] ?? {};
            const isSkipped = override.skip === true;

            const effectiveProjectId =
              override.project_id !== undefined
                ? override.project_id
                : item.project_id;

            // Effective match-level basert på override.
            let level: CommentPreviewItem["match_level"];
            if (isSkipped) level = "skipped";
            else if (override.project_id !== undefined && override.project_id) {
              level = "override";
            } else if (item.match_level === "skipped" && !effectiveProjectId) {
              level = "skipped";
            } else {
              level = item.match_level;
            }

            return (
              <tr
                key={key}
                className={cn(
                  "border-b border-border/40 align-top",
                  isSkipped && "opacity-50",
                )}
              >
                <td className="py-2 px-2 max-w-[360px]">
                  <div className="font-medium break-words leading-snug" title={item.name}>
                    {item.name}
                  </div>
                  {item.group_title && (
                    <div className="text-[10px] text-muted-foreground">
                      {item.group_title}
                    </div>
                  )}
                  {item.skip_reason && level === "skipped" && (
                    <div className="text-[10px] text-muted-foreground italic">
                      {item.skip_reason}
                    </div>
                  )}
                </td>
                <td className="py-2 px-2 tabular-nums">{item.project_code ?? "—"}</td>
                <td className="py-2 px-2">
                  <Select
                    value={effectiveProjectId ?? NONE_VALUE}
                    onValueChange={(v) =>
                      onOverrideChange(key, {
                        project_id: v === NONE_VALUE ? null : v,
                      })
                    }
                    disabled={isSkipped}
                  >
                    <SelectTrigger className="h-7 text-xs px-2">
                      <SelectValue placeholder="Velg prosjekt..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value={NONE_VALUE}>— ingen</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.project_code} — {p.name ?? "(uten navn)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="py-2 px-2 text-right tabular-nums">
                  {item.updates_count || "—"}
                </td>
                <td className="py-2 px-2 text-right tabular-nums">
                  {item.replies_count || "—"}
                </td>
                <td className="py-2 px-2 text-right tabular-nums">
                  {item.attachments_count || "—"}
                </td>
                <td className="py-2 px-2 text-right tabular-nums">
                  {item.likes_count || "—"}
                </td>
                <td className="py-2 px-2 text-center">
                  <input
                    type="checkbox"
                    aria-label={isSkipped ? "Inkluder igjen" : "Skip denne linjen"}
                    checked={isSkipped}
                    onChange={(e) =>
                      onOverrideChange(key, { skip: e.target.checked || undefined })
                    }
                    className="w-3.5 h-3.5 cursor-pointer accent-foreground/70"
                  />
                </td>
                <td className="py-2 px-2">
                  <span
                    className={cn(
                      "inline-block px-1.5 py-0.5 rounded text-[10px] font-medium",
                      MATCH_BADGE[level],
                    )}
                  >
                    {level}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
